// https://github.com/biati-digital/glightbox
import 'glightbox/dist/css/glightbox.css';
import GLightbox from 'glightbox';

const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

const loadScript = (src) =>
	new Promise((resolve, reject) => {
		const existing = document.querySelector(`script[src="${src}"]`);

		if (existing) {
			if (existing.dataset.loaded === 'true') {
				resolve();
				return;
			}

			existing.addEventListener('load', () => resolve(), { once: true });
			existing.addEventListener('error', reject, { once: true });
			return;
		}

		const script = document.createElement('script');
		script.src = src;
		script.async = true;
		script.onload = () => {
			script.dataset.loaded = 'true';
			resolve();
		};
		script.onerror = reject;
		document.head.appendChild(script);
	});

const youtubePostCommand = (iframe, func, args = []) => {
	if (!iframe || !iframe.contentWindow) {
		return;
	}

	iframe.contentWindow.postMessage(
		JSON.stringify({ event: 'command', func, args }),
		'*'
	);
};

const parseVideoUrl = (url) => {
	if (!url) {
		return null;
	}

	if (/\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(url)) {
		return { type: 'file', src: url };
	}

	let match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);

	if (match) {
		return { type: 'youtube', id: match[1] };
	}

	match = url.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);

	if (match) {
		return { type: 'vimeo', id: match[1] };
	}

	return null;
};

const createVideoModal = () => {
	const modal = document.createElement('div');
	modal.className = 'lm-video-modal';
	modal.setAttribute('role', 'dialog');
	modal.setAttribute('aria-modal', 'true');
	modal.setAttribute('aria-hidden', 'true');
	modal.innerHTML = `
		<button type="button" class="lm-video-modal__close" aria-label="Schliessen">&times;</button>
		<button type="button" class="lm-video-modal__unmute" hidden>Ton einschalten</button>
		<div class="lm-video-modal__inner"></div>
	`;

	document.body.appendChild(modal);

	const close = () => {
		modal.classList.remove('is-open');
		modal.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('lm-video-modal-open');
		modal.querySelector('.lm-video-modal__inner').innerHTML = '';
		modal.querySelector('.lm-video-modal__unmute').hidden = true;
	};

	modal.querySelector('.lm-video-modal__close').addEventListener('click', close);
	modal.addEventListener('click', (event) => {
		if (event.target === modal) {
			close();
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && modal.classList.contains('is-open')) {
			close();
		}
	});

	return { modal, close };
};

/**
 * Must run synchronously inside touchstart — iOS drops the user-gesture chain on click/async API.
 */
const openVideoModalSync = (modalApi, url) => {
	const parsed = parseVideoUrl(url);

	if (!parsed) {
		return;
	}

	const inner = modalApi.modal.querySelector('.lm-video-modal__inner');
	const unmuteBtn = modalApi.modal.querySelector('.lm-video-modal__unmute');

	inner.innerHTML = '';
	unmuteBtn.hidden = true;

	modalApi.modal.classList.add('is-open');
	modalApi.modal.setAttribute('aria-hidden', 'false');
	document.body.classList.add('lm-video-modal-open');

	if (parsed.type === 'file') {
		const video = document.createElement('video');
		video.src = parsed.src;
		video.controls = true;
		video.playsInline = true;
		video.setAttribute('playsinline', '');
		video.setAttribute('webkit-playsinline', '');
		video.autoplay = true;
		inner.appendChild(video);

		const playPromise = video.play();

		if (playPromise && typeof playPromise.catch === 'function') {
			playPromise.catch(() => {});
		}

		return;
	}

	const iframe = document.createElement('iframe');
	iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
	iframe.allowFullscreen = true;
	iframe.setAttribute('frameborder', '0');
	iframe.setAttribute('title', 'Video');
	inner.appendChild(iframe);

	// iOS: autoplay in iframe only works muted; unmute via button (new user gesture).
	if (parsed.type === 'youtube') {
		const origin = encodeURIComponent(window.location.origin);
		iframe.src = `https://www.youtube-nocookie.com/embed/${parsed.id}?autoplay=1&playsinline=1&mute=1&rel=0&modestbranding=1&enablejsapi=1&origin=${origin}`;
	} else {
		iframe.src = `https://player.vimeo.com/video/${parsed.id}?autoplay=1&playsinline=1&muted=1`;
	}

	unmuteBtn.hidden = false;

	const enableSound = (event) => {
		event.preventDefault();
		event.stopPropagation();
		unmuteBtn.hidden = true;

		if (parsed.type === 'youtube') {
			const ytIframe = inner.querySelector('iframe');

			if (ytIframe) {
				youtubePostCommand(ytIframe, 'unMute');
				youtubePostCommand(ytIframe, 'setVolume', [100]);
				youtubePostCommand(ytIframe, 'playVideo');
			}

			return;
		}

		loadScript('https://player.vimeo.com/api/player.js').then(() => {
			if (!modalApi.modal.classList.contains('is-open') || !window.Vimeo) {
				return;
			}

			const player = new window.Vimeo.Player(iframe);
			player.setVolume(1).then(() => player.play()).catch(() => {});
			modalApi._player = player;
		});
	};

	unmuteBtn.onclick = null;
	unmuteBtn.addEventListener('touchstart', enableSound, { passive: false, once: true });
	unmuteBtn.addEventListener('click', enableSound, { once: true });
};

const initMobileVideoModal = () => {
	const modalApi = createVideoModal();
	let lastOpenAt = 0;

	const handleOpen = (event) => {
		const link = event.target.closest('a.glightbox-video');

		if (!link || !link.href) {
			return;
		}

		event.preventDefault();
		event.stopImmediatePropagation();

		const now = Date.now();

		if (now - lastOpenAt < 500) {
			return;
		}

		lastOpenAt = now;
		openVideoModalSync(modalApi, link.href);
	};

	// touchstart keeps iOS user-gesture chain; click fires too late for autoplay.
	document.addEventListener('touchstart', handleOpen, { capture: true, passive: false });
};

const initGlightbox = () => {
	if (isTouchDevice) {
		initMobileVideoModal();
	}

	const lightbox = GLightbox({
		selector: isTouchDevice ? '.glightbox:not(.glightbox-video)' : '.glightbox',
		touchNavigation: true,
		autoplayVideos: true,
		autofocusVideos: false,
		plyr: {
			css: 'https://cdn.plyr.io/3.7.8/plyr.css',
			js: 'https://cdn.plyr.io/3.7.8/plyr.js',
			config: {
				ratio: '16:9',
				autoplay: true,
				playsinline: true,
				muted: false,
				hideControls: false,
				clickToPlay: true,
				fullscreen: {
					enabled: true,
					fallback: true,
					iosNative: true,
				},
				youtube: {
					noCookie: true,
					rel: 0,
					showinfo: 0,
					iv_load_policy: 3,
					modestbranding: 1,
					playsinline: 1,
					enablejsapi: 1,
				},
				vimeo: {
					byline: false,
					portrait: false,
					title: false,
					speed: true,
					transparent: false,
					autopause: true,
				},
			},
		},
	});

	const pauseAllPlayers = () => {
		if (typeof lightbox.getAllPlayers !== 'function') {
			return;
		}

		const players = lightbox.getAllPlayers();

		if (!players || !players.length) {
			return;
		}

		players.forEach((player) => {
			if (player && typeof player.pause === 'function') {
				player.pause();
			}
		});
	};

	lightbox.on('close', pauseAllPlayers);

	lightbox.on('slide_changed', ({ prev, current }) => {
		if (
			prev &&
			current &&
			typeof prev.slideIndex === 'number' &&
			prev.slideIndex !== current.slideIndex
		) {
			lightbox.slidePlayerPause(prev.slideIndex);
		}
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initGlightbox);
} else {
	initGlightbox();
}
